import { useState, useEffect, useRef, useCallback } from "react";
import {
  Search,
  X,
  Users,
  Building2,
  Mail,
  FileText,
  Loader2,
} from "lucide-react";
import { colors, fonts } from "../styles/theme";
import { globalSearch } from "../services/api";
import { useNavigate } from "react-router-dom";

const ENTITY_CONFIG = {
  contact: { icon: Users, label: "Contacts", color: "#6366f1" },
  company: { icon: Building2, label: "Companies", color: "#10b981" },
  newsletter: { icon: Mail, label: "Newsletters", color: "#8b5cf6" },
  document: { icon: FileText, label: "Documents", color: "#3b82f6" },
};

export default function GlobalSearchModal({ open, onClose }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Focus input when modal opens
  useEffect(() => {
    if (open) {
      setQuery("");
      setResults(null);
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Debounced search
  useEffect(() => {
    if (!query || query.length < 2) {
      setResults(null);
      return;
    }
    setLoading(true);
    const timer = setTimeout(() => {
      globalSearch(query, 5)
        .then((data) => {
          setResults(data);
          setSelectedIndex(0);
        })
        .catch(() => setResults(null))
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  // Flatten results for keyboard navigation
  const allItems = results
    ? [
        ...results.contacts,
        ...results.companies,
        ...results.newsletters,
        ...results.documents,
      ]
    : [];

  const handleSelect = useCallback(
    (item) => {
      onClose();
      navigate(item.url);
    },
    [navigate, onClose],
  );

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((i) => Math.min(i + 1, allItems.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && allItems[selectedIndex]) {
      handleSelect(allItems[selectedIndex]);
    } else if (e.key === "Escape") {
      onClose();
    }
  };

  if (!open) return null;

  // Group results by type
  const groups = ["contact", "company", "newsletter", "document"]
    .map((type) => ({
      type,
      config: ENTITY_CONFIG[type],
      items: allItems.filter((i) => i.type === type),
    }))
    .filter((g) => g.items.length > 0);

  const totalResults = allItems.length;
  let flatIdx = 0;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        display: "flex",
        justifyContent: "center",
        paddingTop: "15vh",
        background: "rgba(0,0,0,0.5)",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 560,
          maxHeight: "60vh",
          background: colors.bgCard,
          borderRadius: 12,
          border: `1px solid ${colors.border}`,
          boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          alignSelf: "flex-start",
        }}
      >
        {/* Search input */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "14px 16px",
            borderBottom: `1px solid ${colors.border}`,
          }}
        >
          <Search size={18} color={colors.textSecondary} />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search contacts, companies, newsletters, documents..."
            style={{
              flex: 1,
              border: "none",
              outline: "none",
              background: "transparent",
              fontSize: 15,
              fontFamily: fonts.body,
              color: colors.text,
            }}
          />
          {loading && (
            <Loader2
              size={16}
              color={colors.textSecondary}
              className="spin"
            />
          )}
          {query && !loading && (
            <button
              onClick={() => setQuery("")}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: colors.textSecondary,
                display: "flex",
                padding: 2,
              }}
            >
              <X size={16} />
            </button>
          )}
        </div>

        {/* Results */}
        <div style={{ flex: 1, overflowY: "auto", padding: "4px 0" }}>
          {!query || query.length < 2 ? (
            <div
              style={{
                padding: "32px 16px",
                textAlign: "center",
                color: colors.textSecondary,
                fontSize: 13,
              }}
            >
              Type to search across contacts, companies, newsletters...
            </div>
          ) : results && totalResults === 0 && !loading ? (
            <div
              style={{
                padding: "32px 16px",
                textAlign: "center",
                color: colors.textSecondary,
                fontSize: 13,
              }}
            >
              No results for &quot;{query}&quot;
            </div>
          ) : (
            groups.map((group) => {
              const Icon = group.config.icon;
              return (
                <div key={group.type}>
                  {/* Group header */}
                  <div
                    style={{
                      padding: "8px 16px 4px",
                      fontSize: 11,
                      fontWeight: 600,
                      color: colors.textSecondary,
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <Icon size={12} color={group.config.color} />
                    {group.config.label}
                  </div>

                  {/* Items */}
                  {group.items.map((item) => {
                    const idx = flatIdx++;
                    const isSelected = idx === selectedIndex;
                    return (
                      <button
                        key={`${item.type}-${item.id}`}
                        onClick={() => handleSelect(item)}
                        onMouseEnter={() => setSelectedIndex(idx)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          width: "100%",
                          padding: "8px 16px",
                          border: "none",
                          background: isSelected ? colors.bgHover : "transparent",
                          color: colors.text,
                          fontSize: 13,
                          fontFamily: fonts.body,
                          cursor: "pointer",
                          textAlign: "left",
                          transition: "background 0.1s",
                        }}
                      >
                        <div
                          style={{
                            width: 28,
                            height: 28,
                            borderRadius: 6,
                            background: `${group.config.color}15`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <Icon size={14} color={group.config.color} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              fontWeight: 600,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {item.title}
                          </div>
                          {item.subtitle && (
                            <div
                              style={{
                                fontSize: 11,
                                color: colors.textSecondary,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {item.subtitle}
                            </div>
                          )}
                        </div>
                        {isSelected && (
                          <span
                            style={{
                              fontSize: 10,
                              color: colors.textSecondary,
                              flexShrink: 0,
                            }}
                          >
                            Enter
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>

        {/* Footer hint */}
        <div
          style={{
            padding: "8px 16px",
            borderTop: `1px solid ${colors.border}`,
            display: "flex",
            gap: 16,
            fontSize: 11,
            color: colors.textSecondary,
          }}
        >
          <span>
            <kbd style={kbdStyle}>&uarr;</kbd>
            <kbd style={kbdStyle}>&darr;</kbd> navigate
          </span>
          <span>
            <kbd style={kbdStyle}>Enter</kbd> select
          </span>
          <span>
            <kbd style={kbdStyle}>Esc</kbd> close
          </span>
        </div>
      </div>    </div>
  );
}

const kbdStyle = {
  display: "inline-block",
  padding: "1px 5px",
  borderRadius: 3,
  border: "1px solid #ddd",
  background: "#f5f5f5",
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: 10,
  marginRight: 2,
};
